<template>
  <AppMain title="Dashboard">
    <div>
      <div class="max-w-6xl mx-auto mt-8 lg:mt-11 px-4 mb-5 sm:px-6 lg:px-8 sm:flex sm:items-center sm:justify-between">
        <div v-if="!completedOnBoarding">
          <div class="text-lg leading-6 font-medium text-gray-900 inline-flex align-middle">
            <span>🚀 Let's get started with SparkLayer, {{ name }}!</span>
          </div>
        </div>
        <div v-else>
          <h3 class="text-lg leading-6 font-medium text-gray-900">
            👋 Welcome back, {{ name }}!
          </h3>
        </div>
        <div
          v-if="env.platform === 'shopify' && !completedOnBoarding"
          class="mt-3 hidden sm:flex items-center relative justify-center sm:mt-0 sm:ml-4"
        >
          <router-link
            to="/help"
            class="inline-flex relative items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400"
          >
            <CheckCircleIcon class="group-hover:text-spark-purple mr-2 -ml-1 h-5 w-5" />
            <span class="animate-ping absolute inline-flex h-5 w-5 -ml-1 rounded-full bg-green-400 opacity-75" />
            <span>Free setup assistance</span>
          </router-link>
        </div>
      </div>

      <div class="max-w-6xl mx-auto px-4 mt-6 sm:px-6 lg:px-8">
        <div class="bg-white shadow overflow-hidden sm:rounded-md">
          <div
            v-if="!completedOnBoarding"
            class=""
          >
            <div class="bg-white p-4 sm:p-6 border-b border-gray-200">
              <h3 class="leading-6 font-medium text-gray-900 mb-2">
                Onboarding process
              </h3>
              <div class="flex align-middle items-center">
                <p class="text-sm text-gray-500">
                  {{ csvTasksCompleted }} of {{ csvTasksTotal }} tasks complete
                </p>
                <div class="w-full bg-gray-200 rounded-full h-4 flex-1 ml-6">
                  <div
                    x-data="scrollProgress"
                    class="bg-green-500 text-xs font-medium text-center p-0.5 leading-none rounded-l-full"
                    :style="{ width: csvTasksCompletedPercent + '%' }"
                  >
                    <span
                      v-if="!env.onboarding_platform"
                      class="text-gray-500 ml-1"
                    >
                      {{ csvTasksCompletedPercent }}%
                    </span>
                    <span
                      v-else
                      class="text-green-100"
                    >
                      {{ csvTasksCompletedPercent }}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Onboarding v-if="!completedOnBoarding" />
          <div v-else>
            <div class="shadow overflow-hidden rounded-md bg-green-50 text-green-800 border border-green-300">
              <div class="">
                <router-link
                  to="/configuration/onboarding"
                  class="w-full flex items-center"
                >
                  <div class="min-w-0 flex-1 flex items-center justify-between">
                    <div class="relative flex space-x-3">
                      <div class="flex-shrink-0 text-white flex w-[59px] min-w-[64px] items-start justify-center pt-4 bg-green-400">
                        <div>
                          <StatusIcon :status-color="env.onboarding_platform ? 'green' : ''" />
                        </div>
                      </div>
                      <div class="w-3/4 md:w-full p-4 pr-0">
                        <p class="text-sm font-medium md:truncate text-green-800">
                          You're all set up with SparkLayer!
                        </p>
                        <p class="mt-1 flex items-center text-sm text-gray-500">
                          You can check and configure your integration at any point within the
                          Configuration section.
                        </p>
                      </div>
                    </div>
                    <div class="mr-4">
                      <ChevronRightIcon class="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </router-link>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!completedOnBoarding" class="mt-6 grid gap-6 grid-cols-1 sm:grid-cols-2">
          <div class="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
            <div class="h-full block">
              <div class="flex items-stretch h-full">
                <a
                  style="display: flex; background: url('https://i.ibb.co/2hBwQvy/Frame-29.png') no-repeat; background-position: center center; background-size: cover"
                  class="w-1/3 relative flex items-center justify-center cursor-pointer"
                >
                  <span class="sr-only">Get started</span>
                </a>
                <div class="p-6 w-0 flex-1">
                  <dl>
                    <dt class="text-sm font-medium text-gray-600">
                      Install SparkLayer for me
                    </dt>
                    <dd>
                      <div class="mt-1 mb-3 flex items-center text-sm text-gray-500">
                        Our expert team can help integrate SparkLayer onto your website.
                      </div>
                      <a
                        class="inline-flex items-center cursor-pointer trigger-intercom-install px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <ChatIcon class="group-hover:text-spark-purple mr-2 h-5 w-5" />
                        <span>Get started</span>
                      </a>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-white overflow-hidden shadow rounded-md border border-gray-200">
            <div class="h-full block">
              <div class="flex items-stretch h-full">
                <a
                  href="https://docs.sparklayer.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style="display: flex; background: url('https://i.ibb.co/Mk729s3/docs.png') no-repeat; background-position: center center; background-size: cover"
                  class="w-1/3 relative flex items-center justify-center"
                >
                  <span class="sr-only">Visit our Help Docs</span>
                </a>
                <div class="p-6 w-0 flex-1">
                  <dl>
                    <dt class="text-sm font-medium text-gray-600">
                      Visit our Help Docs
                    </dt>
                    <dd>
                      <div class="mt-1 mb-3 flex items-center text-sm text-gray-500">
                        Our Help Docs are packed with tips and tricks on how
                        to implement SparkLayer.
                      </div>
                      <a
                        href="https://docs.sparklayer.io/"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <ExternalLinkIcon class="group-hover:text-spark-purple mr-2 h-5 w-5" />
                        <span>Explore more</span>
                      </a>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-8 my-8 shadow sm:rounded-md sm:overflow-hidden">
          <div class="bg-white p-6 space-y-6 sm:p-10">
            <SalesPerformance v-if="purchaseApiEnabled" />
            <OrdersSalesPerformance v-else />
          </div>
        </div>
        <UpsellPlan v-if="plan === 'v2_basic' || plan === null" class="mt-6 sm:mt-8 mb-10" />
      </div>
    </div>
  </AppMain>
</template>

<script>
import {
  ChevronRightIcon,
  CheckCircleIcon,
  ExternalLinkIcon,
  ChatIcon,
} from '@heroicons/vue/outline';
import { mapGetters } from 'vuex';
import AppMain from '@/layouts/AppMain.vue';
import Onboarding from '@/components/Onboarding.vue';
import OrdersSalesPerformance from '@/views/Orders/partials/OrdersSalesPerformance.vue';
import StatusIcon from '@/components/StatusIcon.vue';
import UpsellPlan from '@/components/UpsellPlan.vue';
import { featureEnabled, features } from '@/features';
import SalesPerformance from '@/components/SalesPerformance.vue';

export default {
  name: 'Home',
  components: {
    SalesPerformance,
    OrdersSalesPerformance,
    Onboarding,
    AppMain,
    ChevronRightIcon,
    StatusIcon,
    ExternalLinkIcon,
    CheckCircleIcon,
    ChatIcon,
    UpsellPlan,
  },
  data() {
    return {
      selectedOnboardingHint: null,
      ONBOARDING_FULL_ASSISTANCE: 'ONBOARDING_FULL_ASSISTANCE',
      ONBOARDING_ASSIST_IF_NEEDED: 'ONBOARDING_ASSIST_IF_NEEDED',
      ONBOARDING_NO_ASSISTANCE: 'ONBOARDING_NO_ASSISTANCE',
    };
  },
  computed: {
    ...mapGetters({
      name: 'auth/firstName',
      isAdvancedFeaturesEnabled: 'auth/isAdvancedFeaturesEnabled',
      env: 'env/get',
      plan: 'auth/plan',
      team: 'auth/team',
    }),
    onBoardingSteps() {
      return [
        'onboarding_platform',
        'onboarding_sync',
        'onboarding_frontend',
        'onboarding_settings',
        'onboarding_launch',
      ];
    },
    completedOnBoarding() {
      return this.csvTasksTotal === this.csvTasksCompleted;
    },
    csvTasksTotal() {
      return this.onBoardingSteps.length;
    },
    csvTasksCompleted() {
      return this.onBoardingSteps.map((x) => this.env[x]).filter(Boolean).length;
    },
    csvTasksCompletedPercent() {
      return Math.round((this.csvTasksCompleted / this.csvTasksTotal) * 100);
    },
    purchaseApiEnabled() {
      return featureEnabled(features.PURCHASE_API, this.team.slugged_name, this.env.platform);
    },
  },
};
</script>
